<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { renderAsync } from "docx-preview";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";

defineOptions({
  name: "documentDocxView",
});

const PDFJS_DPI = 72;
const CSS_DPI = 96;

const WRAPPER_SELECTOR = ".docx-wrapper";
const PAGE_SELECTOR = ".docx-wrapper > section.docx";
const WRAPPER_OVERRIDES = ["!bg-transparent", "!p-0", "!w-fit", "!gap-4"];
const PAGE_OVERRIDES = ["bg-white", "!shadow-lg", "!mb-0"];

const props = withDefaults(
  defineProps<{
    url: string;
    scale?: number;
    dataTestid?: string;
  }>(),
  {
    scale: 0.45,
    dataTestid: "my-students-submission-current-document-docx",
  },
);

const emit = defineEmits<(e: "error") => void>();

const docxViewer = ref<HTMLElement | undefined>(undefined);
const isDocxLoaded = ref<boolean>(false);
const naturalWidth = ref<number | undefined>(undefined);
const naturalHeight = ref<number | undefined>(undefined);

let renderGeneration = 0;

// A transform leaves the element's layout size untouched, so the box is sized from
// the document's own dimensions to keep the scroll container honest.
const isScaled = computed<boolean>(() => !!naturalWidth.value && !!naturalHeight.value);

const renderScale = computed<number>(() => (props.scale * PDFJS_DPI) / CSS_DPI);

const frameStyle = computed<Record<string, string>>((): Record<string, string> => {
  if (!isScaled.value) {
    return {};
  }
  return {
    width: `${(naturalWidth.value as number) * renderScale.value}px`,
    height: `${(naturalHeight.value as number) * renderScale.value}px`,
  };
});

const documentStyle = computed<Record<string, string>>(
  (): Record<string, string> => (isScaled.value ? { transform: `scale(${renderScale.value})` } : {}),
);

const clearViewer = (): void => {
  if (docxViewer.value) {
    docxViewer.value.innerHTML = "";
  }
  naturalWidth.value = undefined;
  naturalHeight.value = undefined;
};

const loadDocx = async (): Promise<void> => {
  if (!props.url) {
    return;
  }
  const generation = ++renderGeneration;
  isDocxLoaded.value = false;
  clearViewer();

  try {
    const response = await fetch(props.url);
    if (!response.ok) {
      throw new Error(`Preview failed with status ${response.status}`);
    }
    const blob = await response.blob();
    if (generation !== renderGeneration || !docxViewer.value) {
      return;
    }

    await renderAsync(blob, docxViewer.value);
    if (generation !== renderGeneration || !docxViewer.value) {
      return;
    }

    const wrapper = docxViewer.value.querySelector<HTMLElement>(WRAPPER_SELECTOR);
    wrapper?.classList.add(...WRAPPER_OVERRIDES);
    for (const page of docxViewer.value.querySelectorAll<HTMLElement>(PAGE_SELECTOR)) {
      page.classList.add(...PAGE_OVERRIDES);
    }
    naturalWidth.value = wrapper?.offsetWidth;
    naturalHeight.value = wrapper?.offsetHeight;
    isDocxLoaded.value = true;
  } catch (reason) {
    Sentry.captureException(reason, {
      tags: {
        troubleshooting_note: "Failed to load docx file on submission for My Students",
      },
    });
    emit("error");
  }
};

watch((): string => props.url, loadDocx);

onMounted(loadDocx);

onBeforeUnmount(() => {
  renderGeneration++;
  clearViewer();
});
</script>

<template>
  <div v-if="url" class="text-start" :data-testid="dataTestid">
    <SkeletonLoader
      v-if="!isDocxLoaded"
      height="h-24"
      data-testid="my-students-submission-current-document-docx-loader"
    />
    <div class="flex flex-col items-center">
      <div class="overflow-hidden" :style="frameStyle">
        <div ref="docxViewer" class="w-fit origin-top-left" :style="documentStyle"></div>
      </div>
    </div>
  </div>
</template>
